import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import Papa from 'papaparse';
import { useParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid-pro';
import { getColumns, Column } from '../../api/spreadsheet';
import { useSnackbar } from '../../context/SnackbarContext';
import { useSpreadsheetViewType } from '../../context/CampaignContext';
import type { SpreadsheetColumnTypeMap } from '../../constants/spreadsheetColumnTypes';
import ModalHeader from './CSVUploadModal/ModalHeader';
import ModalActions, { UploadStep } from './CSVUploadModal/ModalActions';
import UploadStepContent from './CSVUploadModal/UploadStep';
import PreviewMappingStep from './CSVUploadModal/PreviewMappingStep';

interface CSVUploadModalProps {
  open: boolean;
  onClose: () => void;
  onFileSelect: (file: File) => void;
  onMappingComplete?: (
    uploadedRows: Record<string, any>[],
    mapping: Record<string, string | null>,
    customColumnTypes?: SpreadsheetColumnTypeMap
  ) => void;
}

const CSVUploadModal: React.FC<CSVUploadModalProps> = ({
  open,
  onClose,
  onFileSelect,
  onMappingComplete
}) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const [step, setStep] = useState<UploadStep>(UploadStep.UPLOAD);
  const [file, setFile] = useState<File | null>(null);
  const [rawCsvRows, setRawCsvRows] = useState<string[][]>([]);
  const [csvData, setCsvData] = useState<Record<string, any>[]>([]);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [headerRowOffset, setHeaderRowOffset] = useState<number>(0);
  const [columnsDefinition, setColumnsDefinition] = useState<GridColDef[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [currentMapping, setCurrentMapping] = useState<
    Record<string, string | null>
  >({});
  const [customColumnTypes, setCustomColumnTypes] =
    useState<SpreadsheetColumnTypeMap>({});
  const [existingColumnTypes, setExistingColumnTypes] = useState<
    Record<string, string>
  >({});
  const [importing, setImporting] = useState(false);

  // Fetch campaign columns when modal opens
  useEffect(() => {
    if (open && campaignId) {
      const fetchColumns = async () => {
        setLoadingColumns(true);
        try {
          const response = await getColumns(campaignId, viewType);
          const columns: Column[] = (response.columns || []).sort(
            (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
          );
          const gridCols: GridColDef[] = columns.map((col) => ({
            field: col.col_name,
            headerName: col.col_name,
            width: 150,
            sortable: true,
            filterable: false,
            editable: false
          }));
          setColumnsDefinition(gridCols);
          const typesMap: Record<string, string> = {};
          columns.forEach((col) => {
            if (col.col_type) typesMap[col.col_name] = col.col_type;
          });
          setExistingColumnTypes(typesMap);
        } catch (error: any) {
          showSnackbar(
            error?.message || 'Failed to load campaign columns',
            'error'
          );
        } finally {
          setLoadingColumns(false);
        }
      };
      fetchColumns();
    }
  }, [open, campaignId, viewType, showSnackbar]);

  const resetState = () => {
    setStep(UploadStep.UPLOAD);
    setFile(null);
    setRawCsvRows([]);
    setCsvData([]);
    setDetectedColumns([]);
    setHeaderRowOffset(0);
    setCurrentMapping({});
    setCustomColumnTypes({});
    setExistingColumnTypes({});
  };

  const handleFileParsed = async (selectedFile: File) => {
    setFile(selectedFile);
    try {
      // Read file as text to get raw rows
      const text = await selectedFile.text();

      // Parse CSV without header to get raw rows
      return new Promise<void>((resolve, reject) => {
        const rawRows: string[][] = [];
        Papa.parse(text, {
          header: false,
          skipEmptyLines: false,
          complete: (results) => {
            const parsedRows = results.data as string[][];
            // Keep all rows (including empty) so preview indices match file indices
            parsedRows.forEach((row) => {
              if (row && Array.isArray(row)) {
                rawRows.push(row.map((cell) => String(cell ?? '')));
              }
            });

            const hasContent = rawRows.some((r) =>
              r.some((c) => c != null && String(c).trim().length > 0)
            );
            if (rawRows.length === 0 || !hasContent) {
              showSnackbar('CSV file appears to be empty', 'error');
              reject(new Error('Empty CSV file'));
              return;
            }

            setRawCsvRows(rawRows);
            // Automatically parse with default header row (0) and go to preview+mapping
            setHeaderRowOffset(0);
            parseWithHeaderRow(selectedFile, 0);
            setStep(UploadStep.PREVIEW_MAPPING);
            resolve();
          },
          error: (error: Error) => {
            showSnackbar(`Error parsing CSV: ${error.message}`, 'error');
            reject(error);
          }
        });
      });
    } catch (error: any) {
      showSnackbar(
        `Error reading CSV file: ${error?.message || 'Unknown error'}`,
        'error'
      );
    }
  };

  const parseWithHeaderRow = async (fileToParse: File, offset: number) => {
    try {
      // Parse the full file with Papa (no line-splitting). Papa respects quoted fields, so
      // newlines inside "..." stay in one cell. Then skip `offset` parsed rows and use
      // the next row as headers.
      const text = await fileToParse.text();
      Papa.parse(text, {
        header: false,
        skipEmptyLines: false,
        complete: (results) => {
          const rows = results.data as string[][];
          if (!rows || rows.length === 0) {
            showSnackbar('No data found. Please check your file.', 'error');
            return;
          }
          const fromIndex = Math.min(offset, rows.length - 1);
          const headerRow = rows[fromIndex];
          if (!headerRow || !Array.isArray(headerRow)) {
            showSnackbar(
              'No data found after skipping rows. Please check your offset value.',
              'error'
            );
            return;
          }
          // Keep original column indices: skipping empty header cells must not shift row[j].
          const headerColumns: { name: string; colIndex: number }[] = [];
          headerRow.forEach((cell, colIndex) => {
            const name = String(cell ?? '').trim();
            if (name.length > 0) {
              headerColumns.push({ name, colIndex });
            }
          });
          if (headerColumns.length === 0) {
            showSnackbar(
              'No column headers found. Please check your offset value.',
              'error'
            );
            return;
          }
          const headers = headerColumns.map((c) => c.name);
          const data: Record<string, any>[] = [];
          for (let i = fromIndex + 1; i < rows.length; i++) {
            const row = rows[i];
            if (!Array.isArray(row)) continue;
            const record: Record<string, any> = {};
            for (const { name, colIndex } of headerColumns) {
              record[name] =
                row[colIndex] != null ? String(row[colIndex]) : '';
            }
            data.push(record);
          }
          setDetectedColumns(headers);
          setCsvData(data);
        },
        error: () => {
          showSnackbar('Error parsing CSV', 'error');
        }
      });
    } catch (error) {
      showSnackbar('Error processing CSV file', 'error');
    }
  };

  const handleHeaderRowChange = async (newOffset: number) => {
    if (!file || rawCsvRows.length === 0) return;

    if (newOffset >= rawCsvRows.length) {
      showSnackbar(
        `Offset (${newOffset}) exceeds file length. Please use a smaller value.`,
        'error'
      );
      return;
    }

    setHeaderRowOffset(newOffset);
    await parseWithHeaderRow(file, newOffset);
  };

  const handleMappingConfirmed = async () => {
    const allFields = Object.keys(currentMapping);

    const uploadedRows = csvData.map((row) => {
      const newRow: Record<string, any> = {};
      allFields.forEach((field) => {
        const csvCol = currentMapping[field];
        newRow[field] = csvCol ? (row[csvCol] ?? '') : '';
      });
      return newRow;
    });

    if (onMappingComplete) {
      setImporting(true);
      try {
        await onMappingComplete(
          uploadedRows,
          currentMapping,
          customColumnTypes
        );
        if (onFileSelect) {
          onFileSelect(file!);
        }
        resetState();
        onClose();
      } finally {
        setImporting(false);
      }
    } else {
      if (onFileSelect) {
        onFileSelect(file!);
      }
      resetState();
      onClose();
    }
  };

  const handleMappingChange = (mapping: Record<string, string | null>) => {
    setCurrentMapping(mapping);
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    if (step === UploadStep.PREVIEW_MAPPING) {
      setStep(UploadStep.UPLOAD);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={importing ? () => {} : handleCancel}
      maxWidth={step === UploadStep.PREVIEW_MAPPING ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          maxHeight: '90vh',
          position: 'relative'
        }
      }}
    >
      {importing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.85)',
            zIndex: 10,
            borderRadius: '20px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <CircularProgress />
            <Typography color="text.secondary">Importing data...</Typography>
          </Box>
        </Box>
      )}
      <ModalHeader
        title={
          step === UploadStep.UPLOAD
            ? 'Import from CSV File'
            : 'Preview & Map Columns'
        }
        onClose={importing ? () => {} : handleCancel}
      />
      <DialogContent sx={{ px: 3, py: 2 }}>
        {step === UploadStep.UPLOAD && (
          <UploadStepContent onFileParsed={handleFileParsed} />
        )}

        {step === UploadStep.PREVIEW_MAPPING && (
          <PreviewMappingStep
            rawCsvRows={rawCsvRows}
            headerRowOffset={headerRowOffset}
            onHeaderRowChange={handleHeaderRowChange}
            columnsDefinition={columnsDefinition}
            existingColumnTypes={existingColumnTypes}
            detectedColumns={detectedColumns}
            loadingColumns={loadingColumns}
            onMappingChange={handleMappingChange}
            onColumnTypesChange={setCustomColumnTypes}
          />
        )}
      </DialogContent>
      <ModalActions
        step={step}
        onCancel={handleCancel}
        onBack={step === UploadStep.PREVIEW_MAPPING ? handleBack : undefined}
        onConfirm={
          step === UploadStep.PREVIEW_MAPPING
            ? handleMappingConfirmed
            : undefined
        }
        importing={importing}
      />
    </Dialog>
  );
};

export default CSVUploadModal;
